
import { getToolbarButtons } from '../base/config';
import { TILE_VIEW_ENABLED, getFeatureFlag } from '../base/flags';
import { getParticipantCount, getParticipants, getPinnedParticipant } from '../base/participants';
import { isLocalVideoTrackDesktop, isParticipantAudioMuted, isParticipantVideoMuted } from '../base/tracks';
import { CHAT_SIZE } from '../chat/constants';
import {
    ABSOLUTE_MAX_COLUMNS,
    ASPECT_RATIO_BREAKPOINT,
    DEFAULT_MAX_COLUMNS,
    SINGLE_COLUMN_BREAKPOINT,
    TWO_COLUMN_BREAKPOINT
} from '../filmstrip/constants';
import { isVideoPlaying } from '../shared-video/functions';

import { LAYOUTS } from './constants';

declare var APP: Object;
declare var interfaceConfig: Object;
declare var config: Object;

/**
 * Maps layouts to their corresponding button identifiers in toolbarButtons.
 */
export const LAYOUT_BUTTON_MAP = {
    [LAYOUTS.VERTICAL_FILMSTRIP_VIEW]: [ 'filmstrip', 'verticalview', 'verticalfilmstrip', 'vertical' ],
    [LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW]: [ 'horizontalstrip', 'horizontalview', 'horizontalfilmstrip', 'horizontal' ],
    [LAYOUTS.TILE_VIEW]: [ 'tileview', 'tile-view', 'tile' ],
    [LAYOUTS.STAGE_VIEW]: [ 'stageview', 'stage-view', 'stage' ]
};

/**
 * Checks whether a given layout is allowed based on toolbarButtons in config.
 * If no layout buttons are specified in toolbarButtons, all layouts are allowed.
 * If at least one layout button is specified, only layouts present in toolbarButtons are allowed.
 *
 * @param {Object} state - The redux state.
 * @param {string} layout - One of LAYOUTS.*.
 * @returns {boolean}
 */
export function isLayoutAllowed(state: Object, layout: string): boolean {
    const currentState = state || (typeof APP !== 'undefined' && APP.store ? APP.store.getState() : {});
    const buttons = getToolbarButtons(currentState);

    if (!Array.isArray(buttons)) {
        return true;
    }

    const allLayoutButtonNames = [
        ...LAYOUT_BUTTON_MAP[LAYOUTS.VERTICAL_FILMSTRIP_VIEW],
        ...LAYOUT_BUTTON_MAP[LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW],
        ...LAYOUT_BUTTON_MAP[LAYOUTS.TILE_VIEW],
        ...LAYOUT_BUTTON_MAP[LAYOUTS.STAGE_VIEW]
    ];

    const hasAnyLayoutConfigured = buttons.some(btn => allLayoutButtonNames.includes(btn));

    if (!hasAnyLayoutConfigured) {
        return true;
    }

    const targetNames = LAYOUT_BUTTON_MAP[layout] || [];

    return targetNames.some(name => buttons.includes(name));
}

/**
 * Checks whether a given layout is allowed, taking into account responsive fallback exceptions:
 * - On narrow screens (<500px) or PiP mode: Horizontal View is allowed even if not in toolbarButtons.
 * - On short height screens (<440px): Vertical View is allowed even if not in toolbarButtons.
 *
 * @param {Object} state - The redux state.
 * @param {string} layout - One of LAYOUTS.*.
 * @returns {boolean}
 */
export function isResponsiveLayoutAllowed(state: Object, layout: string): boolean {
    if (isLayoutAllowed(state, layout)) {
        return true;
    }

    if (typeof window === 'undefined') {
        return false;
    }

    const currentState = state || (typeof APP !== 'undefined' && APP.store ? APP.store.getState() : {});

    // If NO non-tile layout is allowed at all (only Tile View is in toolbarButtons),
    // never allow non-tile layouts via responsive fallback exceptions.
    const nonTileAllowed = isLayoutAllowed(currentState, LAYOUTS.VERTICAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.STAGE_VIEW);

    if (!nonTileAllowed) {
        return false;
    }

    const isPiP = Boolean(currentState['features/base/conference']?._isPictureInPictureEnabled);
    const isNarrowOrPiP = isPiP || window.innerWidth < 500;

    // Narrow screen or PiP: allow horizontal even if not in toolbar buttons to avoid breaking UI
    if (layout === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW && isNarrowOrPiP) {
        return true;
    }

    const isRecorder = Boolean(currentState['features/base/config']?.iAmRecorder
        || (typeof config !== 'undefined' && config?.iAmRecorder));
    const isShortHeight = !isNarrowOrPiP
        && !isRecorder
        && window.innerHeight < 440
        && window.innerWidth >= 500;

    // Short height: allow vertical even if not in toolbar buttons to avoid breaking UI
    if (layout === LAYOUTS.VERTICAL_FILMSTRIP_VIEW && isShortHeight) {
        return true;
    }

    return false;
}

/**
 * Returns all allowed layouts according to toolbarButtons.
 *
 * @param {Object} state - The redux state.
 * @returns {Array<string>}
 */
export function getAllowedLayouts(state: Object): Array<string> {
    const all = [
        LAYOUTS.VERTICAL_FILMSTRIP_VIEW,
        LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW,
        LAYOUTS.TILE_VIEW,
        LAYOUTS.STAGE_VIEW
    ];

    return all.filter(layout => isLayoutAllowed(state, layout));
}

/**
 * Returns the effective allowed layout given a desired layout and fallback preference,
 * respecting responsive layout exceptions (narrow/PiP -> horizontal, short height -> vertical).
 *
 * @param {Object} state - The redux state.
 * @param {string} [desiredLayout] - The layout desired by the caller.
 * @param {Array<string>} [customPreference] - Optional custom preference order.
 * @returns {string}
 */
export function getEffectiveLayout(state: Object, desiredLayout?: string, customPreference?: Array<string>): string {
    const currentState = state || (typeof APP !== 'undefined' && APP.store ? APP.store.getState() : {});

    // If NO non-tile layout is allowed at all (only Tile View is in toolbarButtons),
    // the layout MUST remain Tile View.
    const nonTileAllowed = isLayoutAllowed(currentState, LAYOUTS.VERTICAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.STAGE_VIEW);

    if (!nonTileAllowed) {
        return LAYOUTS.TILE_VIEW;
    }

    let targetLayout = desiredLayout;

    if (typeof window !== 'undefined') {
        const isPiP = Boolean(currentState['features/base/conference']?._isPictureInPictureEnabled);
        const isNarrowOrPiP = isPiP || window.innerWidth < 500;
        const isRecorder = Boolean(currentState['features/base/config']?.iAmRecorder
            || (typeof config !== 'undefined' && config?.iAmRecorder));
        const isShortHeight = !isNarrowOrPiP
            && !isRecorder
            && window.innerHeight < 440
            && window.innerWidth >= 500;

        if (isNarrowOrPiP && targetLayout === LAYOUTS.VERTICAL_FILMSTRIP_VIEW) {
            targetLayout = LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW;
        } else if (isShortHeight && targetLayout === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW) {
            targetLayout = LAYOUTS.VERTICAL_FILMSTRIP_VIEW;
        }
    }

    if (targetLayout && isResponsiveLayoutAllowed(currentState, targetLayout)) {
        return targetLayout;
    }

    const defaultPreference = [
        LAYOUTS.VERTICAL_FILMSTRIP_VIEW,
        LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW,
        LAYOUTS.STAGE_VIEW,
        LAYOUTS.TILE_VIEW
    ];

    const preferenceList = customPreference || defaultPreference;

    for (const layout of preferenceList) {
        if (isResponsiveLayoutAllowed(currentState, layout)) {
            return layout;
        }
    }

    const allowed = getAllowedLayouts(currentState);

    return allowed.length > 0 ? allowed[0] : targetLayout || LAYOUTS.TILE_VIEW;
}

/**
 * Returns the {@code LAYOUTS} constant associated with the layout
 * the application should currently be in.
 *
 * @param {Object} state - The redux state.
 * @returns {string}
 */
export function getCurrentLayout(state: Object) {
    const currentState = state || (typeof APP !== 'undefined' && APP.store ? APP.store.getState() : {});
    const nonTileAllowed = isLayoutAllowed(currentState, LAYOUTS.VERTICAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.STAGE_VIEW);

    if (!nonTileAllowed) {
        return LAYOUTS.TILE_VIEW;
    }

    const filmStripView = currentState['features/base/conference']?.filmStripView;

    if (filmStripView === LAYOUTS.TILE_VIEW) {
        return LAYOUTS.TILE_VIEW;
    } else if (filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW) {
        return LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW;
    } else if (filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW) {
        return LAYOUTS.VERTICAL_FILMSTRIP_VIEW;
    } else if (filmStripView === LAYOUTS.STAGE_VIEW) {
        return LAYOUTS.STAGE_VIEW;
    }

    if (typeof interfaceConfig !== 'undefined' && interfaceConfig.VERTICAL_FILMSTRIP) {
        return LAYOUTS.VERTICAL_FILMSTRIP_VIEW;
    }

    return LAYOUTS.STAGE_VIEW;
}

/**
 * Returns a list of participants that have their video available and are unmuted.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array<Object>}
 */
export function participantWithVideoUnmuted(state: Object) {
    const participants = getParticipants(state);

    return participants.filter(participant =>
        !isParticipantVideoMuted(participant, state)
    );
}


/**
 * Returns a list of participants that have their audio available and are unmuted.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array<Object>}
 */
export function participantWithAudioUnmuted(state: Object) {
    const participants = getParticipants(state);

    return participants.filter(participant =>
        !isParticipantAudioMuted(participant, state)
    );
}

/**
 * Returns how many columns should be displayed in tile view. The number
 * returned will be between 1 and 7, inclusive.
 *
 * @param {Object} state - The redux store state.
 * @returns {number}
 */
export function getMaxColumnCount(state: Object) {
    const configuredMax = interfaceConfig.TILE_VIEW_MAX_COLUMNS || DEFAULT_MAX_COLUMNS;
    const { disableResponsiveTiles } = state['features/base/config'];

    if (!disableResponsiveTiles) {
        const { clientWidth } = state['features/base/responsive-ui'];
        let availableWidth = clientWidth;
        const participantCount = getParticipantCount(state);
        const { isOpen } = state['features/chat'];

        if (isOpen) {
            availableWidth -= CHAT_SIZE;
        }

        // If there are just two participants in a conference, enforce single-column view for mobile size.
        if (participantCount === 2 && availableWidth < ASPECT_RATIO_BREAKPOINT) {
            return Math.min(1, Math.max(configuredMax, 1));
        }

        // Enforce single column view at very small screen widths.
        if (availableWidth < SINGLE_COLUMN_BREAKPOINT) {
            return Math.min(1, Math.max(configuredMax, 1));
        }

        // Enforce two column view below breakpoint.
        if (availableWidth < TWO_COLUMN_BREAKPOINT) {
            return Math.min(2, Math.max(configuredMax, 1));
        }
    }

    return Math.min(Math.max(configuredMax, 1), ABSOLUTE_MAX_COLUMNS);
}

/**
 * Returns the cell count dimensions for tile view. Tile view tries to uphold
 * equal count of tiles for height and width, until maxColumn is reached in
 * which rows will be added but no more columns.
 *
 * @param {Object} state - The redux store state.
 * @param {number} maxColumns - The maximum number of columns that can be
 * displayed.
 * @returns {Object} An object is return with the desired number of columns,
 * rows, and visible rows (the rest should overflow) for the tile view layout.
 */
export function getTileViewGridDimensions(state: Object) {
    const maxColumns = getMaxColumnCount(state);

    // When in tile view mode, we must discount ourselves (the local participant) because our
    const { iAmRecorder } = state['features/base/config'];
    const numberOfParticipants = state['features/base/participants']?.length - (iAmRecorder ? 1 : 0);

    const columnsToMaintainASquare = Math.ceil(Math.sqrt(numberOfParticipants));
    const columns = Math.min(columnsToMaintainASquare, maxColumns);
    const rows = Math.ceil(numberOfParticipants / columns);
    const visibleRows = Math.min(maxColumns, rows);

    return {
        columns,
        visibleRows
    };
}

/**
 * Selector for determining if the UI layout should be in tile view. Tile view
 * is determined by more than just having the tile view setting enabled, as
 * one-on-one calls should not be in tile view, as well as etherpad editing.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} True if tile view should be displayed.
 */
export function shouldDisplayTileView(state: Object = {}) {
    const currentState = state || (typeof APP !== 'undefined' && APP.store ? APP.store.getState() : {});

    // If Tile View is not allowed by toolbarButtons, it cannot be displayed
    if (!isLayoutAllowed(currentState, LAYOUTS.TILE_VIEW)) {
        return false;
    }

    // If NO non-tile layout is allowed by toolbarButtons (i.e. ONLY Tile View is in toolbarButtons),
    // the conference must remain in Tile View and never exit (even on pin, screenshare, whiteboard, youtube)
    const nonTileAllowed = isLayoutAllowed(currentState, LAYOUTS.VERTICAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(currentState, LAYOUTS.STAGE_VIEW);

    if (!nonTileAllowed) {
        return true;
    }

    const participantCount = getParticipantCount(currentState);

    // In case of a lonely meeting, we don't allow tile view.
    // But it's a special case too, as we don't even render the button,
    // see TileViewButton component.
    if (participantCount < 2) {
        return false;
    }

    const tileViewEnabledFeatureFlag = getFeatureFlag(currentState, TILE_VIEW_ENABLED, true);
    const { disableTileView } = currentState['features/base/config'] || {};

    if (disableTileView || !tileViewEnabledFeatureFlag) {
        return false;
    }

    const { tileViewEnabled } = currentState['features/video-layout'] || {};

    if (tileViewEnabled !== undefined) {
        // If the user explicitly requested a view mode, we
        // do that.
        return tileViewEnabled;
    }

    const { iAmRecorder } = currentState['features/base/config'] || {};

    // const { set_recording_tile_view = true } = currentState['features/base/mtApi']?.pregData;


    // None tile view mode is easier to calculate (no need for many negations), so we do
    // that and negate it only once.
    const shouldDisplayNormalMode = Boolean(

        // Reasons for normal mode:

        // White board Open
        currentState['features/genericiframe']?.visible

        // Editing etherpad
        || currentState['features/etherpad']?.editing

        // We pinned a participant
        || getPinnedParticipant(currentState)

        // It's a 1-on-1 meeting
        || participantCount < 2

        // There is a shared YouTube video in the meeting
        || isVideoPlaying(currentState)

        // We want jibri to use stage view by default
        || iAmRecorder // && !set_recording_tile_view)

        // Only exit Tile View when the LOCAL user shares their screen.
        // Remote screen shares appear as separate tiles in Tile View.
        || isLocalVideoTrackDesktop(currentState)

    );

    return !shouldDisplayNormalMode;
}
